import React, { useEffect } from "react";
import { PRESS_MAILTO, PRESS_NOTE } from "../data/pressEmail";

/**
 * MEDIA INQUIRIES PAGE — /media-inquiries
 *
 * Public-facing one-page media kit for podcast producers, journalists,
 * summit organizers, and speaking engagements. Mirrors LOCKED_MEDIA_KIT_V3.md.
 */

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  ocean: "#25a1c2",
  gold: "#d4af6a",
  cream: "#efeae7",
  mint: "#5EEAD4",
};

const MediaInquiriesPage = () => {
  useEffect(() => {
    document.title = "Press & Media — Temple Franklin, Mind Body Spirit HYGIENE™";

    // SEO
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [key, val] = selector.replace(/[\[\]"]/g, "").split("=");
        el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const description =
      "Press, podcast, and speaking inquiries for Temple Franklin, founder of the Mind Body Spirit HYGIENE™ Method. Bio, credentials, interview topics, and contact.";
    const url = "https://mindbodyspirithygienetools.com/media-inquiries";
    const image = "https://mindbodyspirithygienetools.com/og/media-inquiries-avatar.png";

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", "Press & Media — Temple Franklin, Mind Body Spirit HYGIENE™");
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:type"]', "content", "profile");
  }, []);

  return (
    <div className="min-h-screen" style={{ background: BRAND.cream }} data-testid="media-page">
      {/* HEADER */}
      <header
        className="px-6 pt-16 pb-14 md:pt-24 md:pb-20"
        style={{
          background: `linear-gradient(160deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
          color: BRAND.cream,
        }}
        data-testid="media-hero"
      >
        <div className="max-w-5xl mx-auto grid md:grid-cols-[auto_1fr] gap-10 md:gap-14 items-center">
          <img
            src="/temple-portrait-round.png"
            alt="Temple Franklin portrait"
            className="w-44 h-44 md:w-56 md:h-56 rounded-full mx-auto md:mx-0"
            style={{ boxShadow: `0 20px 50px rgba(0,0,0,0.35), 0 0 0 3px ${BRAND.gold}55` }}
            data-testid="media-portrait"
          />
          <div>
            <p className="font-sans text-xs tracking-[0.4em] uppercase mb-4" style={{ color: BRAND.gold }}>
              Media Inquiries
            </p>
            <h1 className="font-serif tracking-tight leading-[0.95] mb-4" style={{ fontSize: "clamp(2.2rem, 5vw, 3.6rem)" }} data-testid="media-name">
              Temple Franklin
            </h1>
            <p className="font-serif italic text-xl md:text-2xl mb-2" style={{ color: BRAND.cream }}>
              Founder, Mind Body Spirit HYGIENE™ Method
            </p>
            <p
              className="font-sans text-xs tracking-[0.25em] uppercase mb-3"
              style={{ color: BRAND.gold }}
              data-testid="hero-credentials-line"
            >
              Radio Broadcast Veteran · Evidential Medium · Author
            </p>
            <p className="font-serif italic text-lg" style={{ color: BRAND.mint }}>
              "You haven't heard it used like this before."
            </p>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-1 font-sans text-sm" style={{ color: `${BRAND.cream}cc` }}>
              <a href="mailto:mindbodyspirithygiene@gmail.com" className="hover:text-white" data-testid="media-email">
                mindbodyspirithygiene@gmail.com
              </a>
              <span>·</span>
              <a href="https://mindbodyspirithygienetools.com" className="hover:text-white">
                mindbodyspirithygienetools.com
              </a>
              <span>·</span>
              <span>@TempleFranklin</span>
            </div>
          </div>
        </div>
      </header>

      <main className="px-6 py-14 md:py-20">
        <div className="max-w-3xl mx-auto space-y-14 md:space-y-20">

          {/* NEW LANGUAGE */}
          <section data-testid="section-new-language">
            <Eyebrow>A New Language for an Old Human Problem</Eyebrow>
            <p className="font-serif text-lg leading-relaxed mb-4" style={{ color: BRAND.deep }}>
              For decades the self-help industry has sold women transformation, awakening, manifesting, healing, becoming, rising, and breakthroughs. Temple Franklin made a different choice.
            </p>
            <p className="font-serif text-2xl italic mb-4" style={{ color: BRAND.teal }}>
              She named it hygiene.
            </p>
            <p className="font-serif text-lg leading-relaxed mb-4" style={{ color: BRAND.deep }}>
              Mind Body Spirit HYGIENE™ is the first daily practice positioned not as a transformation event, but as something you do every single day, the way you brush your teeth and wash your face. Non-negotiable on purpose. Yours to do, by yourself, for yourself.
            </p>
            <p className="font-serif text-lg leading-relaxed mb-4" style={{ color: BRAND.deep }}>
              No one else can hygiene for you. No coach, no guru, no influencer, no app. The work is yours. Temple's method gives you the language, the framework, and the daily tools. The action belongs to the woman doing it.
            </p>
            <p className="font-serif italic text-xl" style={{ color: BRAND.gold }}>
              That is liberation.
            </p>
          </section>

          {/* ABOUT */}
          <section data-testid="section-about">
            <Eyebrow>About Temple</Eyebrow>
            <p className="font-serif text-lg leading-relaxed mb-5" style={{ color: BRAND.deep }}>
              Temple Franklin is a Radio Broadcast veteran. For fifteen years she created, built, and hosted her own Live International Radio show broadcast from KKNW Seattle, WA — which is to say, she has spent half her life on the other side of the microphone, asking the questions producers wish their guests would answer.
            </p>
            <p className="font-serif text-lg leading-relaxed mb-5" style={{ color: BRAND.deep }}>
              She is the founder of the Mind Body Spirit HYGIENE™ Method and the creator of the A.C.T. NOW Method™. A HeartMath Certified practitioner. An evidential medium and intuitive who has held space for those who have crossed over and communicated with individuals in comas. A world traveler who spent three years as a Team Leader and Activity Leader, holding space for thousands of participants healing in real time.
            </p>
            <p className="font-serif text-lg leading-relaxed mb-5" style={{ color: BRAND.deep }}>
              She writes and teaches for the woman in the messy middle — tired of performing, Boundaryless, a Recovering People pleaser. Separated, divorced, sitting in loss, family betrayal, or grief. The woman looking in the mirror some mornings wondering who she even is anymore, and what the hell that even means or looks like.
            </p>
            <p className="font-serif italic text-lg leading-relaxed mb-6" style={{ color: BRAND.teal }}>
              She has been brought down to the studs. She has built herself back. She is here to share the journey — for those willing to learn and open to listen.
            </p>

            <div
              className="p-7 md:p-9 rounded-2xl mb-6"
              style={{
                background: `${BRAND.teal}0d`,
                borderLeft: `3px solid ${BRAND.gold}`,
              }}
              data-testid="origin-story"
            >
              <p className="font-serif text-xl italic mb-5" style={{ color: BRAND.deep }}>
                For fifty years she searched for a home within herself.
              </p>
              <div className="font-serif text-lg leading-[1.85] space-y-1" style={{ color: BRAND.deep }}>
                <p>Through grief and trauma.</p>
                <p>Through estrangement and betrayal.</p>
                <p>Through Hashimoto's, a breast cancer scare, and the bloodwork that kept coming back "normal" while her body told another story.</p>
                <p>Through the slow erosion of a life she once knew.</p>
                <p>Through meditation teachers, mediumship training, and twenty years of independent study.</p>
                <p>Through every method, every modality, every room full of women looking for the same thing she was.</p>
              </div>
              <p className="font-serif text-lg leading-relaxed mt-6" style={{ color: BRAND.deep }}>
                And then one ordinary morning at the kitchen trash can, three words landed in the middle of her chest:{" "}
                <span className="italic" style={{ color: BRAND.teal }}>I GET TO.</span>
              </p>
              <p className="font-serif text-lg leading-relaxed mt-4" style={{ color: BRAND.deep }}>
                That was the moment everything cracked open and clicked into place.
              </p>
              <p className="font-serif italic text-xl mt-4" style={{ color: BRAND.teal }}>
                The work that became this method.
              </p>
            </div>

            <p className="font-serif text-lg leading-relaxed mb-4" style={{ color: BRAND.deep }}>
              She built it because she needed it. So she became her.
            </p>
            <p className="font-serif italic text-lg leading-relaxed" style={{ color: BRAND.teal }}>
              This is not transformation theater. This is not another gimmick. This is hygiene. Daily. Non-negotiable. Yours to do.
            </p>
          </section>

          {/* WHY DIFFERENT */}
          <section data-testid="section-different">
            <Eyebrow>Why Temple Is Different</Eyebrow>
            <div className="overflow-hidden rounded-2xl border" style={{ borderColor: `${BRAND.teal}33` }} data-testid="diff-table">
              <div className="grid grid-cols-2" style={{ background: BRAND.deep, color: BRAND.cream }}>
                <div className="p-4 font-sans text-xs tracking-[0.25em] uppercase font-semibold">
                  What everyone else sells
                </div>
                <div className="p-4 font-sans text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: BRAND.mint }}>
                  What Temple built
                </div>
              </div>
              {[
                ["Transformation events", "Daily hygiene practice"],
                ["Awakening moments", "Non-negotiable repetition"],
                ["Manifesting and becoming", "Tools you actually do"],
                ["Guru leads the work", "You lead the work"],
                ["Inspirational vibes", "Doable, real"],
                ["Religious tribe required", "Free will and discernment"],
                ["Toxic positivity", "Honest language for grief"],
              ].map(([a, b], idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-2 font-serif text-base md:text-lg"
                  style={{
                    background: idx % 2 === 0 ? "white" : `${BRAND.cream}66`,
                    color: BRAND.deep,
                  }}
                >
                  <div className="p-4 border-r" style={{ borderColor: `${BRAND.teal}1a` }}>{a}</div>
                  <div className="p-4 italic" style={{ color: BRAND.teal }}>{b}</div>
                </div>
              ))}
            </div>
            <p className="font-serif text-lg leading-relaxed mt-6" style={{ color: BRAND.deep }}>
              The Mind Body Spirit HYGIENE™ Method is the first time spiritual and personal practice has been framed as hygiene in the way dental hygiene is hygiene. Temple is not the next motivational voice. She is the woman who renamed the category and its self responsibility.
            </p>
          </section>

          {/* CREDENTIALS */}
          <section data-testid="section-credentials">
            <Eyebrow>Credentials</Eyebrow>
            <ul className="font-serif text-lg leading-relaxed space-y-4" style={{ color: BRAND.deep }}>
              {[
                "Radio Broadcast veteran. 15-year creator, builder, and host of a Live International Radio show broadcast from KKNW Seattle, WA.",
                "Twenty-plus years in Mind Body Spirit Research and Development.",
                "HeartMath Certified Practitioner.",
                "Evidential medium and intuitive. Holding space for those who have crossed over. Communicating with individuals in comas.",
                "Three years traveling the world as a Team Leader and Activity Leader. Held space for thousands of participants in their own healing.",
                "Healing POD work. Energy modalities including Reiki, The Reconnection and 111.",
                "Three years trained with a renowned meditation teacher.",
                "Founder, Mind Body Spirit HYGIENE™ Method.",
                "Creator, A.C.T. NOW Method™ — Awareness, Clarity, Tapped In, No Opportunity Wasted.",
                'Author, "Stop Saying \'I Have To.\' Start Living \'I GET TO.\'" (Tapped In Publishing, 2026) — in two editions, for women and for anyone.',
                "Publisher, Tapped In Publishing.",
              ].map((c, idx) => (
                <li key={idx} className="flex gap-3">
                  <span style={{ color: BRAND.gold, flexShrink: 0, fontSize: "1.1rem" }}>✦</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* BOOK */}
          <section data-testid="section-book">
            <Eyebrow>The Book</Eyebrow>
            <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-10 items-start">
              <img
                src="/book-cover-2026.png"
                alt='Stop saying "I Have To." Start living "I GET TO." — book cover'
                className="w-44 md:w-52 mx-auto md:mx-0 rounded-sm"
                style={{
                  boxShadow: `0 20px 50px ${BRAND.deep}33, 0 0 0 1px ${BRAND.gold}33`,
                }}
                data-testid="media-book-cover"
              />
              <div>
                <p className="font-serif text-2xl leading-tight mb-2" style={{ color: BRAND.deep }}>
                  Stop Saying <span className="italic">"I Have To."</span> Start Living <span className="italic" style={{ color: BRAND.teal }}>"I GET TO."</span>
                </p>
                <p className="font-serif italic text-lg mb-4" style={{ color: BRAND.teal }}>
                  The Mind Body Spirit HYGIENE™ Method
                </p>
                <p className="font-sans text-sm leading-relaxed mb-2" style={{ color: BRAND.deep }}>
                  Paperback · 5.5 × 8.5 · 141 pages · $19.99<br />
                  ISBN 979-8-9964172-0-9 · Tapped In Publishing<br />
                  <span data-testid="media-book-availability">Available now · Instant PDF from this site, $9.99 · Paperback $19.99 from Amazon, Barnes & Noble, Lulu and Ingram</span>
                </p>
                <p className="font-serif text-lg leading-relaxed mt-4" style={{ color: BRAND.deep }}>
                  A book the reader is required to <strong>use</strong>, not just read. Includes the A.C.T. NOW Method™, the Personal Code of Conduct (PCC), and daily Mind, Body, and Spirit hygiene practices. The reader does the work.
                </p>
              </div>
            </div>
          </section>

          {/* TWO EDITIONS */}
          <section data-testid="section-two-editions">
            <Eyebrow>TWO EDITIONS</Eyebrow>
            <p className="font-serif text-lg leading-relaxed mb-4" style={{ color: BRAND.deep }}>
              I wrote it for women. That is what you are told to do — pick your niche, speak to one person. So I did.
            </p>
            <p className="font-serif text-lg leading-relaxed mb-4" style={{ color: BRAND.deep }}>
              Then men started telling me it was theirs too.
            </p>
            <p className="font-serif text-lg leading-relaxed" style={{ color: BRAND.deep }}>
              So there are two now. The original, in the language I first wrote it in. And a second with the door opened wider — the same book, the same tools, the same practice.
            </p>
          </section>

          {/* THE COURSE */}
          <section data-testid="section-course">
            <Eyebrow>THE COURSE</Eyebrow>
            <p className="font-serif text-lg leading-relaxed" style={{ color: BRAND.deep }}>
              Build Your Own: Mind Body Spirit HYGIENE™ — twenty-three short lessons, a pen, and a practice that ends up being yours. $33. Printable pages and an online practice together.
            </p>
          </section>

          {/* INTERVIEW TOPICS */}
          <section data-testid="section-topics">
            <Eyebrow>Speaking & Interview Topics</Eyebrow>
            <p className="font-serif italic text-base mb-6" style={{ color: BRAND.teal }}>
              I offer these as a roadmap. I really do prefer and enjoy the natural flow of an engaging, educational conversation.
            </p>
            <ol className="space-y-3 font-serif text-lg leading-relaxed list-none" style={{ color: BRAND.deep }}>
              {[
                "Why I named it hygiene. The new language reshaping the wellness conversation.",
                "The Kitchen Trash Moment. How three words at 6 AM rewrote a 20-plus-year search.",
                "Brought down to the studs. Who can relate? Why this method works.",
                "I GET TO vs. I Have To. The language that either heals you or eats you alive.",
                "The A.C.T. NOW Method™. A framework for rebuilding from the studs.",
                "The Personal Code of Conduct (PCC). Non-negotiables for yourself, by yourself.",
                "The Messy Middle. Real language for divorce, estrangement, grief, and midlife reckoning.",
                "The end of toxic positivity, spiritual bypass, and identity gatekeeping.",
                "Free will and Divine discernment without religion, without gurus, without bypass.",
                "Why you cannot outsource your own hygiene. The reader does the work.",
                "I wrote it for women. Then men asked. What happens when the people you did not write for tell you it is theirs.",
              ].map((t, idx) => (
                <li key={idx} className="flex gap-4" data-testid={`topic-${idx + 1}`}>
                  <span className="font-sans text-sm font-semibold flex-shrink-0 mt-1" style={{ color: BRAND.gold, minWidth: "1.6rem" }}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* QUOTES */}
          <section data-testid="section-quotes">
            <Eyebrow>Signature Quotes</Eyebrow>
            <div className="space-y-5">
              {[
                "You don't outsource brushing your teeth. You don't outsource your personal hygiene either.",
                "This is hygiene. Daily. Non-negotiable. Yours. How you define it.",
                "Real tools, for real women, in the messy middle.",
              ].map((q, idx) => (
                <blockquote
                  key={idx}
                  className="font-serif italic text-xl md:text-2xl leading-snug pl-6"
                  style={{ borderLeft: `3px solid ${BRAND.gold}`, color: BRAND.deep }}
                  data-testid={`quote-${idx}`}
                >
                  "{q}"
                </blockquote>
              ))}
            </div>
          </section>

          {/* AUDIENCE */}
          <section data-testid="section-audience">
            <Eyebrow>Audience</Eyebrow>
            <p className="font-serif text-lg leading-relaxed mb-5" style={{ color: BRAND.deep }}>
              Women, 35 to 65, navigating the messy middle — looking in the mirror and <em>"I do not know who I am anymore"</em> — divorce, estrangement, grief, midlife rebuilding, or the slow erosion of a life they once knew. Spiritual but not religious. Tired of performance wellness. Looking for daily, doable, honest practice they can take ownership of.
            </p>
            <p className="font-sans text-sm mb-1" style={{ color: BRAND.teal }}>ACTIVE ACROSS</p>
            <ul className="font-serif text-base leading-relaxed space-y-1" style={{ color: BRAND.deep }}>
              <li>Website · <a href="https://mindbodyspirithygienetools.com" className="underline" style={{ color: BRAND.teal }}>mindbodyspirithygienetools.com</a></li>
              <li>YouTube · Tapped In daily card pulls and method teaching</li>
              <li>Instagram · @TempleFranklin</li>
              <li>Blog · <a href="/blog" className="underline" style={{ color: BRAND.teal }}>mindbodyspirithygienetools.com/blog</a></li>
              <li>Email list · 52-week sequence via MailerLite</li>
            </ul>
          </section>

          {/* CONTACT */}
          <section
            className="p-8 md:p-10 rounded-2xl text-center"
            style={{ background: BRAND.deep, color: BRAND.cream }}
            data-testid="section-contact"
          >
            <p className="font-sans text-xs tracking-[0.4em] uppercase mb-5" style={{ color: BRAND.gold }}>
              Contact & Booking
            </p>
            <h2 className="font-serif tracking-tight mb-3" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)" }}>
              For interviews & podcast appearances
            </h2>
            <p className="font-serif italic text-lg mb-7" style={{ color: `${BRAND.cream}cc` }}>
              Press kit and high-resolution book cover available on request.
            </p>
            <div className="max-w-2xl mx-auto mb-8 text-left" data-testid="press-note">
              <p className="font-sans text-xs tracking-[0.3em] uppercase mb-3" style={{ color: BRAND.gold }}>
                A note from Temple:
              </p>
              <p className="font-serif text-lg leading-relaxed" style={{ color: BRAND.cream }}>
                {PRESS_NOTE}
              </p>
              <p className="font-serif text-lg leading-relaxed mt-4" style={{ color: BRAND.cream }} data-testid="press-note-closing">
                Please tell me about your show, and let's see if we are aligned.
              </p>
            </div>
            <a
              href={PRESS_MAILTO}
              className="inline-flex items-center gap-3 px-9 py-4 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] hover:shadow-2xl"
              style={{ background: BRAND.mint, color: BRAND.deep, fontSize: "15px" }}
              data-testid="contact-cta"
            >
              mindbodyspirithygiene@gmail.com →
            </a>
            <p className="font-sans text-xs mt-7 tracking-wide" style={{ color: `${BRAND.cream}88` }}>
              Temple Franklin · mindbodyspirithygienetools.com · @TempleFranklin
            </p>
          </section>

        </div>
      </main>
    </div>
  );
};

const Eyebrow = ({ children }) => (
  <p
    className="font-sans text-xs tracking-[0.4em] uppercase font-semibold mb-5"
    style={{ color: BRAND.gold }}
  >
    {children}
  </p>
);

export default MediaInquiriesPage;
